import { Alert, AutoComplete, Input, Modal, Select, Spin, Switch, Tooltip } from 'antd';
import { DeleteOutlined, GiftOutlined, PlusOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { graphql, parse } from 'src/_shared/services/utils.js';
import RuleBuilder from 'src/_shared/components/rule-builder/RuleBuilder';
import {
	candidateCarriers,
	cloneConfig,
	DEFAULT_INELIGIBLE_NOTE,
	isDirty,
	listAtsEntries,
	parseIdInput,
	readCandidateConfig,
	scaffoldCandidateConfig,
	setEmailDomainEquivalences,
	validateCandidateConfig,
	writeCandidateConfig,
} from 'src/_shared/services/candidate-sync/candidateSyncConfig';
import { readValueSpec } from 'src/_shared/services/job-sync/serde';
import BonusResultControl from '../job-sync/BonusResultControl';
import SaveBar from '../console/SaveBar.jsx';
import Section from '../console/Section.jsx';
import '../job-sync/job-sync.css';

// The candidate feed's normalized keys (claraty candidate contract + this stack's additive extras),
// used to seed the bonus rule builder's field AutoComplete. The requisition guards and Yext's bonus
// tiers key off jobRequisitionId / jobLevel.
const FIELD_SUGGESTIONS = [
	'jobLevel', 'jobTitle', 'jobDepartment', 'jobRequisitionId', 'jobRecruiterEmployeeId', 'jobPostId',
	'uniqueJobId', 'applicationStatus', 'applicationStage', 'applicationStatusMapped', 'applicationStageMapped',
	'applicationSourceId', 'applicationSourceName', 'applicationDate', 'prospect', 'offerHireDate', 'offerStartDate',
];

// Job fields commonly set on engine-created rows. 'type' is deliberately absent — it is the
// erin-connect POST sibling, and the validator rejects it as a default.
const DEFAULT_KEY_SUGGESTIONS = ['status', 'externalSource', 'jobType'];

// How many of a tiered bonus's per-tier amounts the read view spells out before collapsing to "+N".
const AMOUNTS_SHOWN = 2;

function FieldError({ message }) {
	return message ? <div className="cs-err">{message}</div> : null;
}

function SwitchRow({ label, description, checked, onChange, live }) {
	return (
		<div className="js-switchrow">
			<Switch checked={!!checked} onChange={onChange} className={live ? 'js-switch-live' : undefined} />
			<div>
				<div className="js-switchrow-label">{label}</div>
				{description ? <div className="js-switchrow-desc">{description}</div> : null}
			</div>
		</div>
	);
}

// `key = value` rows over jobCreate.defaults, DefaultsEditor's row pattern without its jobs-only
// chrome (no close-source badge — candidate sync has no close semantics anywhere). Values keep
// their original type until touched.
function DefaultsRows({ defaults, onChange }) {
	const seq = useRef(0);
	const nextId = () => (seq.current += 1);
	const [rows, setRows] = useState(() =>
		Object.keys(defaults || {}).map((key) => ({ id: nextId(), key, value: defaults[key] }))
	);

	const commit = (nextRows) => {
		setRows(nextRows);
		const out = {};
		for (const { key, value } of nextRows) {
			if (key === '' || key == null) continue;
			out[key] = value;
		}
		onChange(out);
	};
	const updateRow = (id, partial) => commit(rows.map((r) => (r.id === id ? { ...r, ...partial } : r)));

	return (
		<div>
			{rows.length === 0 && <div className="ei-empty-inline">No defaults set.</div>}
			{rows.map((row) => (
				<div className="js-map-row" key={row.id}>
					<div className="js-map-target js-token">
						<AutoComplete
							value={row.key}
							options={DEFAULT_KEY_SUGGESTIONS.map((k) => ({ value: k }))}
							filterOption={(input, option) => String(option.value).toLowerCase().includes(input.toLowerCase())}
							onChange={(key) => updateRow(row.id, { key })}
							placeholder="field"
							style={{ width: '100%' }}
						/>
					</div>
					<span className="js-kw" aria-hidden>
						=
					</span>
					<div className="js-map-source">
						<div className="js-token" style={{ flex: 1 }}>
							<Input
								value={row.value == null ? '' : String(row.value)}
								onChange={(e) => updateRow(row.id, { value: e.target.value })}
								placeholder="value"
							/>
						</div>
						<Tooltip title="Remove default">
							<button
								type="button"
								className="js-iconbtn js-danger"
								onClick={() => commit(rows.filter((r) => r.id !== row.id))}
								aria-label="Remove default"
							>
								<DeleteOutlined />
							</button>
						</Tooltip>
					</div>
				</div>
			))}
			<div style={{ marginTop: 10 }}>
				<button type="button" className="js-linkbtn" onClick={() => commit([...rows, { id: nextId(), key: '', value: '' }])}>
					<PlusOutlined /> Add default
				</button>
			</div>
		</div>
	);
}

// The candidate Processing tab. Same host contract as JobSyncConfigComponent: the editor page
// header renders the Dry-run/Live toggle through the mode bridge (`onModeState` up,
// `modeActionsRef.current.requestMode` down); dirty state is mirrored up for navigation guards.
// The config itself is the state — no parallel form model — so an untouched load→save round-trip
// re-stringifies the Company blob byte-identically.
export default function CandidateSyncConfigComponent({
	currentUser,
	company,
	tieredBonuses,
	onDirtyChange,
	onModeState,
	modeActionsRef,
}) {
	const companyId = company?.id || currentUser?.companyId;

	const [loading, setLoading] = useState(true);
	const [atsIntegration, setAtsIntegration] = useState([]);
	const [entries, setEntries] = useState([]); // entries that could host a config
	const [carriers, setCarriers] = useState([]); // entries that DO carry one
	const [host, setHost] = useState(null); // { index, atsName } of the entry being edited
	const [config, setConfig] = useState(null);
	const [initialJson, setInitialJson] = useState('');
	const [isNew, setIsNew] = useState(false);
	const [saving, setSaving] = useState(false);
	const [notice, setNotice] = useState(null); // { type, message }
	const seedRef = useRef(0); // bump to force-remount stateful sub-editors on entry switch / reset

	// ---- load ----
	useEffect(() => {
		let alive = true;
		(async () => {
			setLoading(true);
			const res = await graphql({ input: { id: companyId }, query: 'getATSSettingsByCompany' }, 'cognito');
			if (!alive) return;
			const ats = parse(res?.atsIntegration) || [];
			const list = listAtsEntries(ats);
			const found = candidateCarriers(ats);
			setAtsIntegration(ats);
			setEntries(list);
			setCarriers(found);
			// Exactly one carrier is the engine's contract. More than one blocks editing below;
			// none means the create flow, defaulting to the first hostable entry.
			if (found.length === 1) {
				selectHost(ats, found[0].index, found[0].atsName);
			} else if (found.length === 0 && list.length > 0) {
				selectHost(ats, list[0].index, list[0].atsName);
			}
			setLoading(false);
		})();
		return () => {
			alive = false;
		};
	}, [companyId]);

	function selectHost(ats, index, atsName) {
		const existing = readCandidateConfig(ats, index);
		if (existing) {
			const cfg = cloneConfig(existing);
			setConfig(cfg);
			setInitialJson(JSON.stringify(cfg));
			setIsNew(false);
		} else {
			const entry = (parse(ats) || [])[index];
			const cfg = scaffoldCandidateConfig(entry, companyId);
			setConfig(cfg);
			setInitialJson(JSON.stringify(cfg));
			setIsNew(true);
		}
		setHost({ index, atsName });
		setNotice(null);
		seedRef.current += 1;
	}

	const conflict = carriers.length > 1;
	const dirty = config && (isNew || isDirty(config, initialJson));

	const errors = useMemo(() => {
		if (!config) return [];
		const list = validateCandidateConfig(config);
		// The editor-side twin of the engine's organization check: a config claiming another
		// company (a promoted copy, a mis-seed) must never be saved onto this one.
		if (config.companyId && companyId && config.companyId !== companyId) {
			list.push({
				path: 'companyId',
				message: `This config claims company ${config.companyId}, but you are editing ${companyId} — the engine will refuse the mismatch.`,
			});
		}
		return list;
	}, [config, companyId]);
	const errorFor = (path) => {
		const hit = errors.find((e) => e.path === path);
		return hit ? hit.message : null;
	};

	// surface real edits (not the untouched isNew scaffold) so the host's navigation guard
	// doesn't false-fire on open.
	const changed = !!config && isDirty(config, initialJson);
	useEffect(() => {
		if (onDirtyChange) onDirtyChange(changed);
	}, [changed, onDirtyChange]);

	// ---- mode bridge to the host header ----
	useEffect(() => {
		if (onModeState) onModeState(config && !conflict ? config.mode : null);
	}, [config, conflict, onModeState]);
	useEffect(() => {
		if (modeActionsRef) modeActionsRef.current = { requestMode: onModeChange };
	});

	// ---- edit helpers (immutable; omit-when-empty keys stay omitted for byte-faithful saves) ----
	const patch = (partial) => setConfig((c) => ({ ...c, ...partial }));
	const patchIn = (sectionKey, partial) =>
		setConfig((c) => ({ ...c, [sectionKey]: { ...(c[sectionKey] || {}), ...partial } }));
	const dropIn = (sectionKey, key) =>
		setConfig((c) => {
			const section = { ...(c[sectionKey] || {}) };
			delete section[key];
			return { ...c, [sectionKey]: section };
		});

	const ineligible = (config && config.rules && config.rules.ineligiblePriorApplication) || null;
	const patchIneligible = (partial) =>
		setConfig((c) => {
			const current = (c.rules && c.rules.ineligiblePriorApplication) || { enabled: false, alreadySourcedIds: [] };
			return { ...c, rules: { ...c.rules, ineligiblePriorApplication: { ...current, ...partial } } };
		});

	const phone = (config && config.identity && config.identity.phone) || null;
	const patchPhone = (partial) =>
		setConfig((c) => {
			const current = (c.identity && c.identity.phone) || { enabled: false, minDigits: 10, requireNameMatch: true };
			return { ...c, identity: { ...(c.identity || {}), phone: { ...current, ...partial } } };
		});

	const equivGroups =
		(config && config.identity && config.identity.emailDomainEquivalences) || [];
	const setEquivGroups = (groups) =>
		setConfig((c) => ({ ...c, identity: setEmailDomainEquivalences(c.identity, groups) }));

	const setLimit = (key, raw) =>
		setConfig((c) => {
			const limits = { ...(c.limits || {}) };
			const s = String(raw).trim();
			if (s === '') {
				delete limits[key];
			} else {
				limits[key] = /^-?\d+(\.\d+)?$/.test(s) ? Number(s) : s; // invalid input stays visible and flagged
			}
			if (Object.keys(limits).length === 0) {
				const next = { ...c };
				delete next.limits;
				return next;
			}
			return { ...c, limits };
		});

	// ---- mode toggle (confirm the dry-run -> live switch) ----
	function onModeChange(nextMode) {
		if (nextMode === 'live' && config.mode !== 'live') {
			Modal.confirm({
				title: 'Switch candidate processing to Live?',
				content: (
					<span>
						Live lets runs update real referrals — hire-state changes trigger <b>bonus payouts</b> — and PATCH
						attribution into the client&apos;s Greenhouse. It still writes nothing until the stack&apos;s{' '}
						<code>CandidateDryRun</code> parameter is deployed <code>false</code>. Continue?
					</span>
				),
				okText: 'Switch to Live',
				okButtonProps: { danger: true },
				onOk: () => patch({ mode: 'live' }),
			});
		} else {
			patch({ mode: nextMode });
		}
	}

	// ---- save ----
	async function doSave() {
		setSaving(true);
		setNotice(null);
		try {
			// Re-read immediately before writing (last-write-wins on the whole blob is the established
			// atsIntegration semantic) and re-derive the carrier from the FRESH array — the single
			// carrier is the config's address, wherever it sits now.
			const fresh = await graphql({ input: { id: companyId }, query: 'getATSSettingsByCompany' }, 'cognito');
			const freshArr = parse(fresh?.atsIntegration) || atsIntegration;
			const freshCarriers = candidateCarriers(freshArr);
			let index;
			if (freshCarriers.length > 1) {
				throw new Error(
					`Multiple atsIntegration entries carry a candidateSyncConfig (${freshCarriers
						.map((e) => e.atsName)
						.join(', ')}) — refusing to guess. Fix the company record, then reload.`
				);
			} else if (freshCarriers.length === 1) {
				if (isNew) {
					throw new Error(
						'A candidate config appeared on another entry while you were editing — reload to pick it up instead of creating a second one.'
					);
				}
				index = freshCarriers[0].index;
			} else {
				// No carrier on the fresh row: first save (or the config was removed meanwhile).
				// Address the chosen host entry by ATSName, first match, like the job-sync editor.
				index = freshArr.findIndex((e) => e && e.ATSName === host.atsName);
				if (index === -1) {
					throw new Error(`No atsIntegration entry with ATSName="${host.atsName}". Create the integration first.`);
				}
			}
			const nextArr = writeCandidateConfig(freshArr, index, config);
			const saved = await graphql(
				{ input: { companyId, atsIntegration: JSON.stringify(nextArr) }, update: 'updateATSIntegration' },
				'cognito'
			);
			if (saved === undefined) throw new Error('The save request failed.');
			setAtsIntegration(nextArr);
			setEntries(listAtsEntries(nextArr));
			setCarriers(candidateCarriers(nextArr));
			setHost({ index, atsName: host.atsName });
			setInitialJson(JSON.stringify(config));
			setIsNew(false);
			setNotice({ type: 'success', message: 'Candidate-sync configuration saved.' });
		} catch (e) {
			setNotice({ type: 'error', message: e.message || 'Could not save the configuration.' });
		}
		setSaving(false);
	}

	function handleSaveClick() {
		if (errors.length) {
			setNotice({ type: 'error', message: 'Fix the highlighted issues before saving.' });
			return;
		}
		if (config.mode === 'live') {
			Modal.confirm({
				title: isNew ? 'Create this configuration in Live mode?' : 'Save this feed in Live mode?',
				content: (
					<span>
						This config is <b>Live</b>. Once the stack&apos;s <code>CandidateDryRun</code> parameter is also{' '}
						<code>false</code>, runs will update real referrals — hire-state changes trigger <b>bonus payouts</b> —
						and PATCH the client&apos;s Greenhouse.
						{config.rules && config.rules.allowUnhire ? (
							<>
								{' '}
								<b>Un-hiring is enabled</b>: hired referrals can be regressed and paid bonuses clawed back.
							</>
						) : null}{' '}
						Continue?
					</span>
				),
				okText: isNew ? 'Create' : 'Save',
				okButtonProps: { danger: true },
				onOk: doSave,
			});
		} else {
			doSave();
		}
	}

	function handleReset() {
		const cfg = JSON.parse(initialJson);
		setConfig(cfg);
		seedRef.current += 1;
		setNotice(null);
	}

	// Read-view amount suffix for a bonus result — same rules as the jobs editor: per-tier amounts
	// are what ONE user group earns, so they are listed (capped) and never summed, joined by a
	// middot because bonus names carry their own "- $10,000" headline figure.
	const bonusAmountLabel = (name) => {
		const bonus = (tieredBonuses || []).find((b) => b && b.name === name);
		if (!bonus || !Array.isArray(bonus.tiers) || bonus.tiers.length === 0) return null;
		const amounts = [];
		for (const tier of bonus.tiers) {
			try {
				const parsed = typeof tier === 'string' ? JSON.parse(tier) : tier;
				const n = Number(parsed && parsed.amount);
				if (Number.isFinite(n)) amounts.push(n);
			} catch {
				// malformed tier — skip it
			}
		}
		if (amounts.length === 0) return null;
		const shown = amounts.slice(0, AMOUNTS_SHOWN).map((n) => `$${n.toLocaleString()}`);
		const rest = amounts.length - shown.length;
		return rest > 0 ? `${shown.join(' · ')} · +${rest}` : shown.join(' · ');
	};

	// ---- render ----
	if (loading) {
		return (
			<div className="js-loading">
				<Spin />
			</div>
		);
	}

	if (conflict) {
		return (
			<Alert
				type="error"
				showIcon
				message="Multiple integrations carry a candidate config"
				description={`Entries ${carriers.map((e) => e.atsName).join(', ')} each carry a candidateSyncConfig. The engine refuses to run this way and this editor refuses to guess — remove the extra config from the company record, then reload.`}
			/>
		);
	}

	if (!config) {
		return (
			<Alert
				type="info"
				showIcon
				message="No ATS integration found"
				description="Create an ATS integration in the ATS settings first — the candidate-sync configuration attaches to it as a sibling of the job-sync config."
			/>
		);
	}

	const writeback = config.greenhouseWriteback || {};
	const jobCreate = config.jobCreate || {};
	const limits = config.limits || {};

	return (
		<>
			{isNew && (
				<Alert
					style={{ marginBottom: 16 }}
					type="info"
					showIcon
					message="New configuration — not saved yet"
					description="This company has no candidate-sync config. Fill it in and Create to replace the seed script."
				/>
			)}

			{/* host-entry context: picker while creating (several possible hosts), a quiet line otherwise */}
			{isNew && entries.length > 1 ? (
				<div style={{ margin: '-6px 0 24px', display: 'flex', alignItems: 'center', gap: 10 }}>
					<span className="ei-minilabel" style={{ margin: 0 }}>
						Attach to
					</span>
					<div className="js-token" style={{ width: 260 }}>
						<Select
							value={host ? host.atsName : undefined}
							onChange={(name) => {
								const target = entries.find((e) => e.atsName === name);
								if (!target) return;
								if (dirty && changed) {
									Modal.confirm({
										title: 'Discard unsaved changes?',
										okText: 'Discard',
										onOk: () => selectHost(atsIntegration, target.index, target.atsName),
									});
								} else {
									selectHost(atsIntegration, target.index, target.atsName);
								}
							}}
							style={{ width: '100%' }}
							options={entries.map((e) => ({ value: e.atsName, label: e.atsName }))}
						/>
					</div>
					<span className="js-switchrow-desc">The entry whose credentials the write-back will use.</span>
				</div>
			) : (
				<div style={{ margin: '-6px 0 24px' }}>
					<span className="js-muted">
						Attached to the <span className="js-mono">{host && host.atsName}</span> ATS entry
						{entries.find((e) => host && e.index === host.index && e.hasJobSyncConfig)
							? ' — a sibling of its job-sync config.'
							: '.'}
					</span>
				</div>
			)}

			<Section
				id="cs-mode"
				title="Mode"
				description="Whether a run only plans (dry-run) or applies its plan. Candidate writes are payout-grade, so two independent gates must open before anything is written."
			>
				<div
					className={`js-modetoggle${config.mode === 'live' ? ' is-live' : ''}`}
					role="radiogroup"
					aria-label="Candidate sync mode"
				>
					<button
						type="button"
						role="radio"
						aria-checked={config.mode !== 'live'}
						className={`js-modeopt${config.mode !== 'live' ? ' is-selected' : ''}`}
						onClick={() => onModeChange('dry-run')}
					>
						Dry-run
					</button>
					<button
						type="button"
						role="radio"
						aria-checked={config.mode === 'live'}
						className={`js-modeopt${config.mode === 'live' ? ' is-selected' : ''}`}
						onClick={() => onModeChange('live')}
					>
						<span className="js-modeopt-dot" aria-hidden />
						Live
					</button>
				</div>
				<FieldError message={errorFor('mode')} />
				<Alert
					style={{ marginTop: 14 }}
					type={config.mode === 'live' ? 'warning' : 'info'}
					showIcon
					message="Live writes are dual-gated"
					description={
						<>
							This per-company mode is only the first gate. The engine also honors the stack parameter{' '}
							<span className="js-mono">CandidateDryRun</span> — even in Live, nothing is written until an
							operator deploys <span className="js-mono">CandidateDryRun=false</span>. Dry-run at either gate
							means plan-only: every intended action still lands in the run&apos;s plan artifact.
						</>
					}
				/>
			</Section>

			<Section
				id="cs-pull"
				title="Pull"
				description="The ERIN referral statuses loaded as the match pool. Every other status still loads as a read-only guard tier — visible to the planner's guards, never matchable."
			>
				<div className="ei-fitem">
					<span className="ei-flabel">Referral statuses</span>
					<div className="js-token js-tags-accent">
						<Select
							mode="tags"
							value={(config.pull && config.pull.referralStatuses) || []}
							onChange={(vals) => patchIn('pull', { referralStatuses: vals })}
							tokenSeparators={[',']}
							placeholder="accepted, interviewing, notHired"
							style={{ width: '100%' }}
							open={false}
						/>
					</div>
					<FieldError message={errorFor('pull.referralStatuses')} />
					<div className="js-switchrow-desc" style={{ marginTop: 6 }}>
						Workato pulled exactly these three: <span className="js-mono">accepted</span>,{' '}
						<span className="js-mono">interviewing</span>, <span className="js-mono">notHired</span>.
					</div>
				</div>
			</Section>

			<Section id="cs-rules" title="Rules" description="Status derivation and Workato's bonus-eligibility rule.">
				<div className="ei-fgrid">
					<div className="ei-fitem is-narrow">
						<span className="ei-flabel">Fallback status</span>
						<div className="js-token">
							<Input
								value={(config.rules && config.rules.fallbackStatus) || ''}
								onChange={(e) => patchIn('rules', { fallbackStatus: e.target.value })}
								placeholder="interviewing"
								status={errorFor('rules.fallbackStatus') ? 'error' : undefined}
							/>
						</div>
						<FieldError message={errorFor('rules.fallbackStatus')} />
						<div className="js-switchrow-desc" style={{ marginTop: 6 }}>
							Applied when the integration&apos;s status mappings produce nothing for a row; unmapped combos are
							surfaced on every run. Workato: <span className="js-mono">interviewing</span>.
						</div>
					</div>
				</div>

				<Alert
					style={{ marginTop: 14 }}
					type="info"
					showIcon
					message="Status mappings live on the integration source"
					description="The stage table itself (raw Greenhouse status/stage → ERIN status) is the CLAUS integration's Status mappings section on the Integration (source) tab — it is not part of this processing config."
				/>

				<div className="ei-minilabel has-rule">Ineligible prior application</div>
				<div className="js-switchlist">
					<SwitchRow
						label="Flag referrals with a pre-existing application"
						description="Workato loop-1 case #1: the candidate already had an active application before the referral was submitted, and it wasn't attributed to a referral source — the referral is marked ineligible for a bonus, with an admin note."
						checked={!!(ineligible && ineligible.enabled)}
						onChange={(v) => patchIneligible({ enabled: v })}
					/>
				</div>
				{ineligible && ineligible.enabled && (
					<>
						<div className="ei-fitem" style={{ marginTop: 14 }}>
							<span className="ei-flabel">Already-sourced ids</span>
							<div className="js-token js-tags-accent">
								<Select
									mode="tags"
									value={(Array.isArray(ineligible.alreadySourcedIds) ? ineligible.alreadySourcedIds : []).map(
										(v) => String(v)
									)}
									onChange={(vals) => patchIneligible({ alreadySourcedIds: vals.map((v) => parseIdInput(v)) })}
									tokenSeparators={[',']}
									placeholder="13, 4370"
									style={{ width: '100%' }}
									open={false}
								/>
							</div>
							<FieldError message={errorFor('rules.ineligiblePriorApplication.alreadySourcedIds')} />
							<div className="js-switchrow-desc" style={{ marginTop: 6 }}>
								Greenhouse source ids that do <b>not</b> disqualify — the self/referral sources. Yext:{' '}
								<span className="js-mono">13, 4370</span>.
							</div>
						</div>
						<div className="ei-fitem" style={{ marginTop: 14 }}>
							<span className="ei-flabel">Admin note</span>
							<div className="js-token">
								<Input.TextArea
									value={ineligible.adminNoteTemplate == null ? '' : ineligible.adminNoteTemplate}
									onChange={(e) => {
										const v = e.target.value;
										if (v === '') {
											setConfig((c) => {
												const current = { ...(c.rules.ineligiblePriorApplication || {}) };
												delete current.adminNoteTemplate;
												return { ...c, rules: { ...c.rules, ineligiblePriorApplication: current } };
											});
										} else {
											patchIneligible({ adminNoteTemplate: v });
										}
									}}
									placeholder={DEFAULT_INELIGIBLE_NOTE}
									autoSize={{ minRows: 2, maxRows: 5 }}
								/>
							</div>
							<div className="js-switchrow-desc" style={{ marginTop: 6 }}>
								Written onto the flagged referral. Leave blank to use Workato&apos;s exact note (the placeholder
								text); <span className="js-mono">{'{applyDate}'}</span> becomes the application date.
							</div>
						</div>
					</>
				)}
			</Section>

			<Section
				id="cs-identity"
				title="Identity"
				description="How a feed candidate is matched to an ERIN person: ATS id first, then email, then — only if enabled — phone. Every action records its match basis."
			>
				<div className="ei-minilabel">Email domain equivalences</div>
				<div className="js-switchrow-desc" style={{ marginBottom: 10 }}>
					Domain groups treated as the same mailbox when matching emails:{' '}
					<span className="js-mono">someone@careers.yext.com</span> is{' '}
					<span className="js-mono">someone@yext.com</span>.
				</div>
				{equivGroups.length === 0 && <div className="ei-empty-inline">No equivalence groups.</div>}
				{equivGroups.map((group, i) => (
					// eslint-disable-next-line react/no-array-index-key
					<div className="cs-eqrow" key={`eq-${i}-${seedRef.current}`}>
						<div className="js-token js-tags-accent" style={{ flex: 1 }}>
							<Select
								mode="tags"
								value={Array.isArray(group) ? group : []}
								onChange={(vals) => setEquivGroups(equivGroups.map((g, j) => (j === i ? vals : g)))}
								tokenSeparators={[',', ' ']}
								placeholder="yext.com, careers.yext.com"
								style={{ width: '100%' }}
								open={false}
							/>
						</div>
						<Tooltip title="Remove group">
							<button
								type="button"
								className="js-iconbtn js-danger"
								onClick={() => setEquivGroups(equivGroups.filter((_, j) => j !== i))}
								aria-label="Remove domain group"
							>
								<DeleteOutlined />
							</button>
						</Tooltip>
					</div>
				))}
				<FieldError message={errorFor('identity.emailDomainEquivalences')} />
				<div style={{ marginTop: 10 }}>
					<button type="button" className="js-linkbtn" onClick={() => setEquivGroups([...equivGroups, []])}>
						<PlusOutlined /> Add domain group
					</button>
				</div>

				<div className="ei-minilabel has-rule">Phone matching</div>
				<div className="js-switchlist">
					<SwitchRow
						label="Match by phone (last resort)"
						description="Workato never matched on phone. Shared and recycled numbers make it the riskiest basis for payout-triggering writes, so a phone match must be the ONLY hit on the last digits — ambiguous numbers are skipped."
						checked={!!(phone && phone.enabled)}
						onChange={(v) => patchPhone({ enabled: v })}
					/>
					{phone && phone.enabled && (
						<SwitchRow
							label="Require last-name corroboration"
							description="A phone-only match also needs the candidate's last name to agree with the contact's."
							checked={phone.requireNameMatch !== false}
							onChange={(v) => patchPhone({ requireNameMatch: v })}
						/>
					)}
				</div>
				{phone && phone.enabled && (
					<div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 14 }}>
						<span className="ei-minilabel" style={{ margin: 0 }}>
							Minimum digits
						</span>
						<div className="js-token" style={{ maxWidth: 120 }}>
							<Input
								value={phone.minDigits == null ? '' : String(phone.minDigits)}
								onChange={(e) => {
									const digits = e.target.value.replace(/\D/g, '');
									if (digits === '') {
										setConfig((c) => {
											const next = { ...(c.identity.phone || {}) };
											delete next.minDigits;
											return { ...c, identity: { ...c.identity, phone: next } };
										});
									} else {
										patchPhone({ minDigits: Number(digits) });
									}
								}}
								placeholder="10"
								inputMode="numeric"
							/>
						</div>
						<span className="js-switchrow-desc">Shorter values are too ambiguous to trust. Default 10.</span>
					</div>
				)}
			</Section>

			<Section
				id="cs-writeback"
				title="Greenhouse write-back"
				description="When an application arrives unattributed, the engine PATCHes source and credited-to back into Greenhouse using the entry's Harvest API key — the referral source for resolved referrers, the self source for self-applications and unresolved transfer referrers, on behalf of an admin user."
			>
				<div className="js-switchlist">
					<SwitchRow
						label="Write attribution back to Greenhouse"
						description="User lookups (reads) still run in dry-run so the plan equals live behavior."
						checked={writeback.enabled === true}
						onChange={(v) => patchIn('greenhouseWriteback', { enabled: v })}
					/>
				</div>
				<FieldError message={errorFor('greenhouseWriteback.enabled')} />
				{writeback.enabled === true && (
					<>
						<div className="ei-fgrid" style={{ marginTop: 14 }}>
							<div className="ei-fitem is-narrow">
								<span className="ei-flabel">Referral source id</span>
								<div className="js-token">
									<Input
										value={writeback.referralSourceId == null ? '' : String(writeback.referralSourceId)}
										onChange={(e) => patchIn('greenhouseWriteback', { referralSourceId: parseIdInput(e.target.value) })}
										placeholder="e.g. 200606"
										status={errorFor('greenhouseWriteback.referralSourceId') ? 'error' : undefined}
									/>
								</div>
								<FieldError message={errorFor('greenhouseWriteback.referralSourceId')} />
								<div className="js-switchrow-desc" style={{ marginTop: 6 }}>
									The &quot;employee referral&quot; source, sent with credited-to.
								</div>
							</div>
							<div className="ei-fitem is-narrow">
								<span className="ei-flabel">Self source id</span>
								<div className="js-token">
									<Input
										value={writeback.selfSourceId == null ? '' : String(writeback.selfSourceId)}
										onChange={(e) => patchIn('greenhouseWriteback', { selfSourceId: parseIdInput(e.target.value) })}
										placeholder="e.g. 4370"
										status={errorFor('greenhouseWriteback.selfSourceId') ? 'error' : undefined}
									/>
								</div>
								<FieldError message={errorFor('greenhouseWriteback.selfSourceId')} />
								<div className="js-switchrow-desc" style={{ marginTop: 6 }}>
									Self-applications, and the fallback on transfers whose referrer can&apos;t be resolved.
								</div>
							</div>
							<div className="ei-fitem is-narrow">
								<span className="ei-flabel">On-Behalf-Of user id</span>
								<div className="js-token">
									<Input
										value={writeback.onBehalfOfUserId == null ? '' : String(writeback.onBehalfOfUserId)}
										onChange={(e) => patchIn('greenhouseWriteback', { onBehalfOfUserId: parseIdInput(e.target.value) })}
										placeholder="e.g. 1732969"
										status={errorFor('greenhouseWriteback.onBehalfOfUserId') ? 'error' : undefined}
									/>
								</div>
								<FieldError message={errorFor('greenhouseWriteback.onBehalfOfUserId')} />
								<div className="js-switchrow-desc" style={{ marginTop: 6 }}>
									Harvest requires an acting admin user.
								</div>
							</div>
						</div>
						<Alert
							style={{ marginTop: 14 }}
							type="info"
							showIcon
							message="Per-tenant Greenhouse ids"
							description="These ids belong to this client's Greenhouse tenant — they live here in configuration, never in code. Copy them from the client's Greenhouse (or the Workato recipe being replaced), not from another company's config."
						/>
					</>
				)}
			</Section>

			<Section
				id="cs-jobcreate"
				title="Job create"
				description="Applications can reference jobs the job sync never created (closed or confidential requisitions). When enabled, the missing ERIN job is created first — status closed, so the job board and the job sync's close-missing never see it."
				bare
			>
				<div className="js-card">
					<div className="js-switchlist">
						<SwitchRow
							label="Create missing jobs"
							description="Created rows carry the same compound external id the job sync uses, so both pipelines address the same job."
							checked={jobCreate.enabled === true}
							onChange={(v) => patchIn('jobCreate', { enabled: v })}
						/>
					</div>
					<FieldError message={errorFor('jobCreate.enabled')} />

					<div className="ei-minilabel has-rule">Defaults</div>
					<div className="js-switchrow-desc" style={{ marginBottom: 10 }}>
						Fields applied to every created job.
					</div>
					<DefaultsRows
						key={`defaults-${seedRef.current}`}
						defaults={jobCreate.defaults || {}}
						onChange={(next) => patchIn('jobCreate', { defaults: next })}
					/>
					<FieldError message={errorFor('jobCreate.defaults')} />
					<FieldError message={errorFor('jobCreate.defaults.type')} />

					<div className="ei-minilabel has-rule">Requisition guards</div>
					<div className="js-switchrow-desc" style={{ marginBottom: 10 }}>
						Both test the Greenhouse <b>requisition</b> id (feed key{' '}
						<span className="js-mono">jobRequisitionId</span>), not the numeric job id.
					</div>
					<div className="ei-fgrid">
						<div className="ei-fitem">
							<span className="ei-flabel">Never create</span>
							<div className="js-token js-tags-accent">
								<Select
									mode="tags"
									value={(config.rules && config.rules.skipJobIds) || []}
									onChange={(vals) =>
										vals.length ? patchIn('rules', { skipJobIds: vals }) : dropIn('rules', 'skipJobIds')
									}
									tokenSeparators={[',']}
									placeholder="PerfReview"
									style={{ width: '100%' }}
									open={false}
								/>
							</div>
							<div className="js-switchrow-desc" style={{ marginTop: 6 }}>
								Requisition ids that never become ERIN jobs. Yext: <span className="js-mono">PerfReview</span>.
							</div>
						</div>
						<div className="ei-fitem">
							<span className="ei-flabel">Never transfer onto (prefixes)</span>
							<div className="js-token js-tags-accent">
								<Select
									mode="tags"
									value={(config.rules && config.rules.skipTransferJobPrefixes) || []}
									onChange={(vals) =>
										vals.length
											? patchIn('rules', { skipTransferJobPrefixes: vals })
											: dropIn('rules', 'skipTransferJobPrefixes')
									}
									tokenSeparators={[',']}
									placeholder="Evergreen"
									style={{ width: '100%' }}
									open={false}
								/>
							</div>
							<div className="js-switchrow-desc" style={{ marginTop: 6 }}>
								No transfer targets a job whose requisition id starts with one of these. Yext:{' '}
								<span className="js-mono">Evergreen</span>.
							</div>
						</div>
					</div>
				</div>

				<RuleBuilder
					key={`bonus-${seedRef.current}`}
					title="Bonus by job level"
					icon={<GiftOutlined />}
					value={jobCreate.bonus || { rules: [] }}
					onChange={(next) => patchIn('jobCreate', { bonus: next })}
					outLabel="bonus"
					fieldOptions={FIELD_SUGGESTIONS}
					truncateAt={1}
					note="bonus name → resolved to a tiered bonus by erin-connect"
					renderResult={(spec, onSpec) => (
						<BonusResultControl spec={spec} onChange={onSpec} tieredBonuses={tieredBonuses} />
					)}
					renderResultRead={(spec) => {
						const { value: bonusName } = readValueSpec(spec);
						if (bonusName == null || bonusName === '') return <span className="js-kw">not set</span>;
						const amounts = bonusAmountLabel(String(bonusName));
						return (
							<>
								<span className="js-res-name">{String(bonusName)}</span>
								{amounts ? <span className="js-res-amt">{`· ${amounts}`}</span> : null}
							</>
						);
					}}
				/>
				<FieldError message={errorFor('jobCreate.bonus')} />
			</Section>

			<Section id="cs-safety" title="Safety" description="Circuit breakers for a systemic mis-match, and the hired guard.">
				<div className="ei-fgrid">
					<div className="ei-fitem is-narrow">
						<span className="ei-flabel">Max actions</span>
						<div className="js-token">
							<Input
								value={limits.maxActions == null ? '' : String(limits.maxActions)}
								onChange={(e) => setLimit('maxActions', e.target.value)}
								placeholder="no cap"
								inputMode="numeric"
								status={errorFor('limits.maxActions') ? 'error' : undefined}
							/>
						</div>
						<FieldError message={errorFor('limits.maxActions')} />
						<div className="js-switchrow-desc" style={{ marginTop: 6 }}>
							Abort the whole apply (loudly) when a run plans more than this — never mass-write on a systemic
							mis-match. Yext ships at <span className="js-mono">50</span>.
						</div>
					</div>
					<div className="ei-fitem is-narrow">
						<span className="ei-flabel">Max hired writes</span>
						<div className="js-token">
							<Input
								value={limits.maxHiredWrites == null ? '' : String(limits.maxHiredWrites)}
								onChange={(e) => setLimit('maxHiredWrites', e.target.value)}
								placeholder="no cap"
								inputMode="numeric"
								status={errorFor('limits.maxHiredWrites') ? 'error' : undefined}
							/>
						</div>
						<FieldError message={errorFor('limits.maxHiredWrites')} />
						<div className="js-switchrow-desc" style={{ marginTop: 6 }}>
							Extra-tight bound on the payout-triggering action kind. Yext: <span className="js-mono">3</span>.
						</div>
					</div>
				</div>

				<div className="ei-minilabel has-rule">Hired referrals</div>
				<div className="js-switchlist">
					<SwitchRow
						label="Allow un-hiring"
						description="Hired referrals are read-only by default — Workato's pull excluded them, so it could never regress one, and un-hiring claws back the referrer's paid bonus. Opt in per client, eyes open."
						checked={!!(config.rules && config.rules.allowUnhire)}
						onChange={(v) => {
							if (!v) {
								dropIn('rules', 'allowUnhire');
								return;
							}
							Modal.confirm({
								title: 'Allow un-hiring hired referrals?',
								content: (
									<span>
										A live run could then move a referral <b>out of hired</b> — regressing the hire and{' '}
										<b>clawing back the paid bonus</b>. This stays off for every Workato-parity client.
									</span>
								),
								okText: 'Allow un-hire',
								okButtonProps: { danger: true },
								onOk: () => patchIn('rules', { allowUnhire: true }),
							});
						}}
						live
					/>
				</div>
			</Section>

			{notice && (
				<Alert
					style={{ marginTop: 14 }}
					type={notice.type}
					showIcon
					closable
					message={notice.message}
					onClose={() => setNotice(null)}
				/>
			)}

			<SaveBar
				tone={isNew ? 'new' : dirty ? 'dirty' : 'clean'}
				statusText={isNew ? 'Not created yet' : dirty ? 'Unsaved changes' : 'All changes saved'}
				sub="Candidate processing saves to the company record — the CLAUS integration itself is the Integration (source) tab."
				errors={errors}
				onReset={handleReset}
				onSave={handleSaveClick}
				resetDisabled={!dirty || isNew}
				saveDisabled={!dirty || errors.length > 0}
				saving={saving}
				saveLabel={isNew ? 'Create configuration' : 'Save changes'}
				live={config.mode === 'live'}
			/>
		</>
	);
}
